import { notEmail } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Divider,
  FieldMessage,
  Notice,
  Stack,
  Text,
} from 'braid-design-system';
import React, {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type MouseEvent,
  type Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { TEALIUM_TEXT_INPUT_LIST_UPDATE } from 'src/app/constants/dispatcherId';
import { sendContentSafetyTrackingEvents } from 'src/app/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import { sendPerUnitBillingValidationTrackingEvents } from 'src/app/dataAnalytics/utils/sendPerUnitBillingValidationTrackingEvents';
import {
  FEATURE_FILTER_CONTENT,
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { textInputList as dispatchTextInputList } from 'src/app/state/dispatcher';
import tealium from 'src/app/state/dispatcher/tealium';
import {
  type Validators,
  filterContent,
  validateField,
} from 'src/app/utils/validation';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { CharacterCounter } from 'src/app/views/shared/CharacterCounter';
import type AppState from 'src/types/appState';
import type { TextInputListRef } from 'src/types/formFields';

import translations from './.vocab';
import { AddItemButton } from './components/AddItemButton';
import type { ItemType } from './components/Item';
import { ItemList } from './components/ItemList';

interface TextInputListProps {
  id: string;
  buttonLabel: string;
  errorMessage?: string;
  maxItemLimit?: number;
  onBlurTrackingEvent: () => void;
  onRemoveItemTrackingEvent: () => void;
}

export const TextInputList = forwardRef(
  (props: TextInputListProps, ref: Ref<TextInputListRef>) => {
    const { t } = useTranslations(translations);
    const {
      id,
      buttonLabel,
      errorMessage,
      maxItemLimit,
      onBlurTrackingEvent,
      onRemoveItemTrackingEvent,
    } = props;
    const {
      items = [],
      jobLocationCountry,
      hirerNation,
    } = useSelector(
      (state: AppState) => ({
        items: state?.formData && state?.formData[id],
        jobLocationCountry: state.session.jobLocationCountry,
        hirerNation: state.session?.hirerAccount?.nation,
      }),
      shallowEqual,
    );
    const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
    const dispatch = useDispatch();
    const isFilterContentEnabled = useFeatureToggleData(FEATURE_FILTER_CONTENT);
    const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);
    const [isValid, setIsValid] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const { getLanguage } = useConfig();

    useImperativeHandle(ref, () => ({ handleValidation }));

    useEffect(() => {
      const itemsLength = items.length;
      if (itemsLength > 0) {
        const newItem = items[itemsLength - 1];
        document.getElementById(newItem.id)?.focus();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    const addItem = () => {
      if (typeof maxItemLimit !== 'number' || items.length < maxItemLimit) {
        dispatch(
          dispatchTextInputList({
            action: 'ADD',
            item: null,
            target: id.toUpperCase(),
          }),
        );
      }
    };

    const filterItems = (currentTarget: string) =>
      items.filter((it: ItemType) => it.id === currentTarget)[0];

    const handleAddItem = (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      addItem();
    };

    const handleEditItem = (event: ChangeEvent<HTMLTextAreaElement>) => {
      event.preventDefault();
      const item = filterItems(event.target.name);
      const updatedItem = {
        ...item,
        value: event.target.value.trimStart(),
      };
      dispatch(
        dispatchTextInputList({
          action: 'EDIT',
          item: updatedItem,
          target: id.toUpperCase(),
        }),
      );
    };

    const handleOnBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
      event.preventDefault();
      dispatch(
        dispatchTextInputList({
          action: 'ON_BLUR',
          item: filterItems(event.target.name),
          target: id.toUpperCase(),
        }),
      );

      setIsValid(true);
      setErrorMsg('');

      dispatch(
        tealium(TEALIUM_TEXT_INPUT_LIST_UPDATE, {
          count: items.filter((obj: ItemType) => obj.value !== '').length,
          target: id.toUpperCase(),
        }),
      );

      onBlurTrackingEvent();
    };

    const handleOnFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
      event.preventDefault();
      dispatch(
        dispatchTextInputList({
          action: 'ON_FOCUS',
          item: filterItems(event.target.name),
          target: id.toUpperCase(),
        }),
      );
    };

    const handleRemoveItem = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      dispatch(
        dispatchTextInputList({
          action: 'REMOVE',
          item: filterItems(event.currentTarget.name),
          target: id.toUpperCase(),
        }),
      );
      dispatch(
        tealium(TEALIUM_TEXT_INPUT_LIST_UPDATE, {
          count: items.length - 1,
          target: id.toUpperCase(),
        }),
      );

      onRemoveItemTrackingEvent();
    };

    const handleValidation = (skipIfNull = false) => {
      if (skipIfNull && items.length === 0) {
        return null;
      }

      let totalLengthOfItems = 0;
      for (const item of items) {
        totalLengthOfItems = totalLengthOfItems + item.value.length;
        if (item.value.length > 300) {
          setIsValid(false);
          setErrorMsg(t('text-input-list-validation-max-char'));
          return t('text-input-list-validation-max-char');
        }
        let validators: Validators = [];
        if (isFilterContentEnabled) validators = [...validators, filterContent];
        if (isPerUnitBilling) validators = [...validators, notEmail];

        const validationResult = validateField(
          {
            field: 'jobDescriptionGuided',
            validators,
            validatorProps: {
              country,
              sendTrackingEvents: sendContentSafetyTrackingEvents,
            },
            value: item.value ?? '',
          },
          skipIfNull,
          getLanguage(),
          [sendPerUnitBillingValidationTrackingEvents],
        );
        if (validationResult !== null) {
          setIsValid(false);
          setErrorMsg(validationResult);
          return validationResult;
        }
      }

      if (errorMessage && totalLengthOfItems === 0) {
        setIsValid(false);
        setErrorMsg(errorMessage);
        return errorMessage;
      }
      dispatch(
        dispatchTextInputList({
          action: 'FILTER_INVALID_ITEMS',
          item: null,
          target: id.toUpperCase(),
        }),
      );

      return null;
    };

    return (
      <Stack space="medium">
        <ItemList
          handleEditItem={handleEditItem}
          handleOnBlur={handleOnBlur}
          handleOnFocus={handleOnFocus}
          handleRemoveItem={handleRemoveItem}
          items={items}
        />
        <Stack space="small">
          {items.length === maxItemLimit ? (
            <Notice tone="info">
              <Text>{t('text-input-list-notice-text')}</Text>
            </Notice>
          ) : (
            <AddItemButton handleAddItem={handleAddItem} label={buttonLabel} />
          )}
          {!isValid && (
            <FieldMessage
              id={`itemList${id}`}
              message={errorMsg}
              tone="critical"
            />
          )}
        </Stack>

        <Stack space="xsmall">
          <Divider />
          {maxItemLimit !== undefined && (
            <Box position="relative" paddingY="small" textAlign="right">
              <CharacterCounter
                currentLength={items.length}
                maxLength={maxItemLimit}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  },
);
